import {CSSProperties, FC, ReactNode, useCallback, useEffect, useState} from 'react';
import {styled} from 'styled-components';

import {normalizeString} from '@shared/lib/utils/string_utils';
import {Door} from '@shared/models/api_models';

import {UnthemedButton} from '@shared_web/components/core/button';
import {showAlert, showSuccess} from '@shared_web/components/core/notifications';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {Textarea} from '@shared_web/components/core/textarea';

import {Translate, translate} from '@mavismart_frontend/components/translate';
import {icon_check} from '@mavismart_frontend/icons/icon_check';
import {icon_cross} from '@mavismart_frontend/icons/icon_cross';
import {icon_pencil2} from '@mavismart_frontend/icons/icon_pencil2';
import {apiCall} from '@mavismart_frontend/lib/api';
import {INPUT_THEME, MAIN_COLOR, TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';
import {updateDoorCustomAddress} from '@mavismart_frontend/stores/all_doors_store';

interface DoorCustomAddressProps {
  door: Door;
  searchQuery?: string;
  hasMatch?: boolean;
}

export const DoorCustomAddress: FC<DoorCustomAddressProps> = ({door, searchQuery, hasMatch}) => {
  const account = useAccount();

  const [customAddress, setCustomAddress] = useState<string | undefined>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const previousCustomAddress = door?.customAddress;

  useEffect(() => setCustomAddress(door.customAddress), [door]);

  // Helper to highlight matching text in a line
  const highlightLine = useCallback(
    (line: string): ReactNode => {
      if (!hasMatch || searchQuery === undefined || !line) {
        return line;
      }

      const searchStr = normalizeString(searchQuery);
      const normalizedLine = normalizeString(line);
      const normalizedMatchIndex = normalizedLine.indexOf(searchStr);

      if (normalizedMatchIndex === -1) {
        return line;
      }

      // Find the match in the original string (case-insensitive)
      const searchLower = searchQuery.toLowerCase();
      const lineLower = line.toLowerCase();
      let matchIndex = lineLower.indexOf(searchLower);

      // If direct search doesn't work, try to find it by character position approximation
      if (matchIndex === -1) {
        matchIndex = 0;
        for (let i = 0; i <= line.length - searchQuery.length; i++) {
          const substr = line.slice(i, i + searchQuery.length);
          if (normalizeString(substr) === searchStr) {
            matchIndex = i;
            break;
          }
        }
      }

      const beforeMatch = line.slice(0, matchIndex);
      const matchText = line.slice(matchIndex, matchIndex + searchQuery.length);
      const afterMatch = line.slice(matchIndex + searchQuery.length);

      return (
        <>
          {beforeMatch}
          <HighlightSpan>{matchText}</HighlightSpan>
          {afterMatch}
        </>
      );
    },
    [hasMatch, searchQuery]
  );

  const handlePencilClick = useCallback(() => {
    setIsEditing(!isEditing);
  }, [isEditing]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setCustomAddress(previousCustomAddress ?? undefined);
  }, [previousCustomAddress]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCustomAddress(e.target.value);
  }, []);

  const handleSave = useCallback(async () => {
    if (!door || customAddress === undefined || isSaving) {
      return;
    }

    // If nothing changed, behave like cancel
    if (customAddress === previousCustomAddress) {
      setIsEditing(false);
      setCustomAddress(previousCustomAddress ?? undefined);
      return;
    }

    setIsSaving(true);
    try {
      await apiCall('POST /update-door-custom-address', {
        doorId: door.id,
        newCustomAddress: customAddress,
        previousCustomAddress,
      });
      showSuccess(translate('door_custom_address_updated', getUserLanguage(account)));
      updateDoorCustomAddress(door.id, customAddress);
      setIsEditing(false);
    } catch {
      showAlert(translate('error_updating_door_custom_address', getUserLanguage(account)));
      setCustomAddress(previousCustomAddress);
    } finally {
      setIsSaving(false);
    }
  }, [account, customAddress, door, isSaving, previousCustomAddress]);

  if (!(isEditing || customAddress)) {
    return (
      <Row>
        <NotProvidedValue>
          <Translate value="no_custom_address" />
        </NotProvidedValue>
        <SvgIcon icon={icon_pencil2} size={14} color="transparent" onClick={handlePencilClick} />
      </Row>
    );
  }

  const isMultiline = (customAddress ?? '').includes('\n');

  if (!isEditing) {
    return (
      <Row $alignItems={isMultiline ? 'flex-end' : 'center'}>
        <span>
          {(customAddress ?? '').split('\n').map((line, idx) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: index is important here
              key={`${line}-${idx}`}
            >
              {highlightLine(line)}
              <br />
            </span>
          ))}
        </span>
        <StyledSvgIcon
          $multiline={isMultiline}
          icon={icon_pencil2}
          size={14}
          color="transparent"
          onClick={handlePencilClick}
        />
      </Row>
    );
  }

  return (
    <Column>
      <StyledTextarea
        value={customAddress ?? ''}
        rows={3}
        onChange={handleChange}
        resize="vertical"
        themeOverrides={{textarea: INPUT_THEME}}
        disabled={isSaving}
        focusOnMount
      />
      <ActionsRow>
        <UnthemedButtonStyled onClick={handleSave} disabled={isSaving}>
          <SvgIcon icon={icon_check} size={22} color="transparent" />
        </UnthemedButtonStyled>
        <UnthemedButtonStyled onClick={handleCancel} disabled={isSaving}>
          <SvgIcon icon={icon_cross} size={22} color={MAIN_COLOR} />
        </UnthemedButtonStyled>
      </ActionsRow>
    </Column>
  );
};

DoorCustomAddress.displayName = 'DoorCustomAddress';

const Row = styled.div<{$alignItems?: CSSProperties['alignItems']}>`
  display: flex;
  align-items: ${p => p.$alignItems ?? 'center'};
  gap: 10px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 10px;
`;

const UnthemedButtonStyled = styled(UnthemedButton)`
  background: none;
  padding: 0;

  &[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const NotProvidedValue = styled.span`
  font-style: italic;
  opacity: 0.8;
  color: ${TEXT_MAIN_COLOR};
`;

const StyledTextarea = styled(Textarea)`
  margin-top: 4px;
`;

const StyledSvgIcon = styled(SvgIcon)<{$multiline?: boolean}>`
  ${p =>
    p.$multiline &&
    `position: relative;
    top: -4px;
    `}
`;

const HighlightSpan = styled.span`
  background-color: #ffff0077;
  color: #000000dd;
  border-radius: 3px;
  padding: 1px 0;
  font-weight: 600;
`;
